import { OTPInput, OTPInputContext, REGEXP_ONLY_DIGITS } from 'input-otp'
import { createContext, forwardRef, useContext, useMemo } from 'react'
import { composeClasses } from '@dd3tech/wiggui'
import { Divider } from '@dd3tech/wiggui/Divider'
import type { OTPInputProps } from 'input-otp'

interface InputOTPContextValue {
  error?: boolean
}

const InputOTPErrorContext = createContext<InputOTPContextValue>({
  error: false
})

interface InputOTPProps extends Omit<OTPInputProps, 'render' | 'children'> {
  error?: boolean
  children: React.ReactNode
  maxLength: number
  helperText?: string
}

const InputOTP = forwardRef<HTMLInputElement, InputOTPProps>(
  (
    {
      className,
      containerClassName,
      error,
      disabled,
      children,
      helperText,
      ...props
    },
    ref
  ) => {
    const contextValue = useMemo(() => ({ error }), [error])

    return (
      <InputOTPErrorContext.Provider value={contextValue}>
        <div className="flex flex-col gap-1">
          <OTPInput
            ref={ref}
            containerClassName={composeClasses(
              'flex items-center gap-2 has-[:disabled]:opacity-50',
              containerClassName
            )}
            className={composeClasses('disabled:cursor-not-allowed', className)}
            disabled={disabled}
            {...props}
          >
            {children}
          </OTPInput>
          {helperText && (
            <p
              className={composeClasses(
                'text-body-s',
                error ? 'text-destructive' : 'text-secondary'
              )}
            >
              {helperText}
            </p>
          )}
        </div>
      </InputOTPErrorContext.Provider>
    )
  }
)
InputOTP.displayName = 'InputOTP'

const InputOTPGroup = forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>(({ className, ...props }, ref) => (
  <div
    ref={ref}
    className={composeClasses('flex items-center gap-2', className)}
    {...props}
  />
))
InputOTPGroup.displayName = 'InputOTPGroup'

const InputOTPSlot = forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement> & { index: number }
>(({ index, className, ...props }, ref) => {
  const inputOTPContext = useContext(OTPInputContext)
  const errorContext = useContext(InputOTPErrorContext)
  const slot = inputOTPContext.slots[index]

  if (!slot) {
    return null
  }

  const { char, hasFakeCaret, isActive } = slot

  return (
    <div
      ref={ref}
      className={composeClasses(
        'relative flex h-12 w-10 items-center justify-center rounded-lg text-body-l text-secondary ring-1 ring-primary transition-all',
        isActive && 'ring-2 !ring-button-primary',
        errorContext.error && 'ring-1 !ring-destructive',
        className
      )}
      {...props}
    >
      {char}
      {hasFakeCaret && (
        <div className="pointer-events-none absolute inset-0 flex items-center justify-center">
          <div className="h-4 w-0 animate-caret-blink border border-button-primary" />
        </div>
      )}
    </div>
  )
})
InputOTPSlot.displayName = 'InputOTPSlot'

const InputOTPSeparator = () => (
  <Divider className="max-h-[2px] min-h-[2px] min-w-4 max-w-4" />
)
InputOTPSeparator.displayName = 'InputOTPSeparator'

export {
  InputOTP,
  InputOTPGroup,
  InputOTPSlot,
  InputOTPSeparator,
  REGEXP_ONLY_DIGITS
}
export type { InputOTPProps }
